import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface Review {
  id: string;
  user_name: string;
  rating: number;
  comment: string;
  created_at: string;
}

interface ListingDetail {
  id: string;
  name: string;
  description: string;
  category: string;
  author_name: string;
  pricing_model: string;
  price_usd?: number;
  rating_avg: number;
  rating_count: number;
  install_count: number;
  status: string;
  solution_url?: string;
  documentation_url?: string;
  created_at: string;
  reviews: Review[];
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`h-4 w-4 ${star <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30 fill-muted-foreground/30'}`}
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function formatPrice(model: string, price?: number): string {
  if (model === 'free') return 'Free';
  if (model === 'subscription') return `$${price?.toFixed(2) ?? '0'}/mo`;
  return `$${price?.toFixed(2) ?? '0'}`;
}

export function MarketplaceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['marketplace-listing', id],
    queryFn: () => api<ListingDetail>(`marketplace/listings/${id}`),
    enabled: !!id,
  });

  const installMutation = useMutation({
    mutationFn: () => api(`marketplace/listings/${id}/install`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-listing', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="h-8 w-48 bg-muted animate-pulse rounded mb-4" />
        <div className="h-64 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="rounded-lg border bg-card p-12 text-center">
          <p className="text-muted-foreground mb-4">Failed to load listing details.</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="text-sm text-primary hover:underline"
          >
            Back to Marketplace
          </button>
        </div>
      </div>
    );
  }

  const listing = data;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate('/marketplace')}
        className="text-sm text-muted-foreground hover:text-foreground mb-6 inline-flex items-center gap-1"
      >
        &larr; Back to Marketplace
      </button>

      {/* Header */}
      <div className="rounded-lg border bg-card p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">{listing.name}</h1>
            <p className="text-sm text-muted-foreground">by {listing.author_name}</p>
          </div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {listing.category}
          </span>
        </div>

        <p className="text-sm text-foreground/80 mb-6 whitespace-pre-wrap">{listing.description}</p>

        {/* Stats row */}
        <div className="flex items-center gap-6 mb-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <StarRating rating={listing.rating_avg} />
            <span>
              {listing.rating_avg.toFixed(1)} ({listing.rating_count} review{listing.rating_count !== 1 ? 's' : ''})
            </span>
          </div>
          <span>{listing.install_count.toLocaleString()} install{listing.install_count !== 1 ? 's' : ''}</span>
          <span className="font-medium text-foreground">
            {formatPrice(listing.pricing_model, listing.price_usd)}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => installMutation.mutate()}
            disabled={installMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {installMutation.isPending
              ? 'Installing...'
              : listing.pricing_model === 'free'
                ? 'Install'
                : 'Purchase & Install'}
          </button>
          {listing.documentation_url && (
            <a
              href={listing.documentation_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-md text-sm font-medium border hover:bg-accent transition-colors"
            >
              Documentation
            </a>
          )}
        </div>

        {installMutation.isError && (
          <p className="text-sm text-red-500 mt-3">
            {installMutation.error instanceof Error ? installMutation.error.message : 'Installation failed. Please try again.'}
          </p>
        )}
        {installMutation.isSuccess && (
          <p className="text-sm text-green-500 mt-3">Successfully installed!</p>
        )}
      </div>

      {/* Reviews */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Reviews ({listing.reviews?.length ?? 0})
        </h2>
        {!listing.reviews || listing.reviews.length === 0 ? (
          <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
            No reviews yet. Be the first to review this solution.
          </div>
        ) : (
          <div className="space-y-3">
            {listing.reviews.map((review) => (
              <div key={review.id} className="rounded-lg border bg-card p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground">{review.user_name}</span>
                    <StarRating rating={review.rating} />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{review.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
